import React from 'react';
import {cn} from '@rocket-ui/tools';
import {Link} from 'react-router-dom';
import {useFetchBanners, useHideBanner, type Banner} from '@rocket-ui/lk-api';
import {CloseIcon} from 'shared/icons';
import {ReceiptInfoDrawer} from './ReceiptInfoDrawer';
import {ReceiptBannerIcon} from './ReceiptBannerIcon';
import styles from './Banners.module.css';

interface BannersProps {
	className?: string;
}

function isInternalPath(url: string): boolean {
	return url.startsWith('/') && !url.startsWith('//');
}

function bannerBody(body: string): string {
	return body.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
}

function bannerPreview(body: string): {preview: string; hasMore: boolean} {
	const normalized = bannerBody(body);
	const breakAt = normalized.indexOf('\n');

	if (breakAt === -1) {
		return {preview: normalized, hasMore: false};
	}

	const rest = normalized.slice(breakAt + 1).trim();

	return {
		preview: normalized.slice(0, breakAt).trim(),
		hasMore: rest.length > 0,
	};
}

function BannerDetailsLink({url}: {url: string}): React.ReactElement {
	if (isInternalPath(url)) {
		return (
			<Link to={url} className={styles.primary}>
				Как это работает
			</Link>
		);
	}

	return (
		<a href={url} className={styles.primary} target="_blank" rel="noreferrer">
			Как это работает
		</a>
	);
}

function BannerCard({
	banner,
	onHide,
	isHiding,
	onOpenDetails,
}: {
	banner: Banner;
	onHide: () => void;
	isHiding: boolean;
	onOpenDetails: () => void;
}): React.ReactElement {
	const {preview, hasMore} = bannerPreview(banner.body);
	const buttonUrl = banner.buttonUrl?.trim() || null;
	const showActions = hasMore || Boolean(buttonUrl) || banner.mayHide;

	return (
		<article className={styles.card}>
			{banner.mayHide ? (
				<button type="button" className={styles.close} onClick={onHide} disabled={isHiding} aria-label="Больше не показывать">
					<CloseIcon className={styles.closeIcon} />
				</button>
			) : null}
			<div className={styles.main}>
				<div className={styles.text}>
					<h2 className={styles.title}>{banner.title}</h2>
					<p className={styles.body}>{preview}</p>
				</div>
				{showActions ? (
					<div className={styles.actions}>
						{hasMore ? (
							<button type="button" className={styles.primary} onClick={onOpenDetails}>
								Как это работает
							</button>
						) : buttonUrl ? (
							<BannerDetailsLink url={buttonUrl} />
						) : null}
						{banner.mayHide ? (
							<button type="button" className={styles.dismiss} onClick={onHide} disabled={isHiding}>
								Больше не показывать
							</button>
						) : null}
					</div>
				) : null}
			</div>
			<ReceiptBannerIcon className={styles.icon} />
		</article>
	);
}

export function Banners({className}: BannersProps): React.ReactElement | null {
	const {data} = useFetchBanners();
	const {mutate, isLoading, variables} = useHideBanner();
	const [detailsBanner, setDetailsBanner] = React.useState<Banner | null>(null);
	const [detailsOpen, setDetailsOpen] = React.useState(false);
	const banners = data?.data ?? [];

	if (banners.length === 0) return null;

	return (
		<>
			<section className={cn(styles.list, className)} aria-label="Объявления">
				{banners.map((banner) => (
					<BannerCard
						key={banner.id}
						banner={banner}
						isHiding={isLoading && variables === banner.id}
						onHide={() => mutate(banner.id)}
						onOpenDetails={() => {
							setDetailsBanner(banner);
							setDetailsOpen(true);
						}}
					/>
				))}
			</section>
			<ReceiptInfoDrawer
				open={detailsOpen}
				title={detailsBanner?.title ?? ''}
				body={detailsBanner ? bannerBody(detailsBanner.body) : ''}
				onClose={() => setDetailsOpen(false)}
			/>
		</>
	);
}
